import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient'; // Your Supabase client
import {
  Container, Box, Typography, Button, CircularProgress, Alert, Paper, TextField, Grid
} from '@mui/material';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function CreateTeamForm() {
  const navigate = useNavigate();
  const [teamName, setTeamName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [userCompanyId, setUserCompanyId] = useState(null);

  useEffect(() => {
    // Fetch the user's company_id to ensure they can create a team
    const fetchUserCompany = async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', session.user.id)
          .single(); // User must have a profile and company_id by this stage

        if (profileError || !profile || !profile.company_id) {
          setError('You must be associated with a company to create a team. Please complete company setup.');
          // Optional: redirect to company setup or dashboard if company_id is missing
          // setTimeout(() => navigate('/onboarding/create-company'), 3000);
          setLoading(false);
          return;
        }
        setUserCompanyId(profile.company_id);
      } else {
        setError('User session not found. Please log in.');
        navigate('/login');
      }
      setLoading(false);
    };
    fetchUserCompany();
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!teamName.trim()) {
      setError('Team name is required.');
      return;
    }
    if (!userCompanyId) {
        setError('Company association not found. Cannot create team.');
        return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('User session not found. Please log in.');
      }
      const token = session.access_token;
      const apikey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;

      const payload = {
        team_name: teamName,
        description: description || null,
      };

      const response = await fetch(`${BACKEND_URL}/functions/v1/onboarding-create-initial-team`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'apikey': apikey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        // Check for RLS policy violation error
        const errorMessage = result.error || 'Failed to create team.';
        if (errorMessage.includes('row-level security policy') || 
            errorMessage.includes('violates row-level security') ||
            errorMessage.includes('permission denied')) {
          throw new Error('Only company administrators can create teams. Please contact your company admin to create a new team for you.');
        }
        throw new Error(errorMessage);
      }

      setMessage(`Team '${result.name}' created successfully! You can create another or finish.`);
      setTeamName(''); // Clear form for another team
      setDescription('');
      // No automatic navigation here, user can create more or click finish.

    } catch (err) {
      console.error('Error creating team:', err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    navigate('/fileupload'); // Navigate to the main app (e.g., file upload or dashboard)
  };

  if (loading && !userCompanyId && !error) {
    // Show a general loading indicator while fetching company ID
    return (
        <Container component="main" maxWidth="sm">
            <Paper elevation={3} sx={{ marginTop: 8, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress />
                <Typography sx={{mt: 2}}>Loading user information...</Typography>
            </Paper>
        </Container>
    );
  }

  return (
    <Container component="main" maxWidth="lg" sx={{ py: 6 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
              Create Your First Team (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Organize users and documents by creating teams within your company.
            </Typography>

            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{message}</Alert>}

            {!userCompanyId && !loading && !error && (
                <Alert severity="warning" sx={{width: '100%', mb: 2}}>Verifying company association...</Alert>
            )}

            {userCompanyId && (
              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField margin="normal" required fullWidth id="teamName" label="Team Name" name="teamName" autoFocus value={teamName} onChange={(e) => setTeamName(e.target.value)} disabled={loading} />
                <TextField margin="normal" fullWidth id="description" label="Team Description (Optional)" name="description" multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={loading} />
                <Button type="submit" fullWidth variant="contained" color="primary" disabled={loading} sx={{ mt: 3, mb: 1, py: 1.5 }}>
                  {loading ? <CircularProgress size={24} /> : 'Create Team'}
                </Button>
              </Box>
            )}
            <Button fullWidth variant="outlined" onClick={handleFinish} sx={{ mt: 2 }} disabled={loading && !!userCompanyId}>
              Finish Setup & Go to Dashboard
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 2, height: { xs: 320, md: 560 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 520 }}>
              <Box component="img" src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=1200&auto=format&fit=crop" alt="Team setup" sx={{ width: '100%', borderRadius: 2, display: 'block' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default CreateTeamForm; 